import type { RegistrationRules, Stay } from '../reserva/types'
import type { FieldRules, GuestData, GuestField } from './types'

/**
 * Reglas de los campos del parte de viajeros. Portadas sin cambios de la app actual:
 * src/pages/checkin/components/manualGuestDataForm/ManualGuestDataForm.tsx (+ .utils.ts),
 * .../countryFormInputs/CountryFormInputs.tsx, src/utils/validators.ts y src/utils/regex.ts.
 * No inventan datos: deciden qué campo se muestra y cuál es obligatorio con lo que ya devuelve E1.
 */

/** Ids de tipo de documento del PMS (app actual: IdentifierTypeId). */
export const IDENTIFIER_TYPE = {
  dni: '6D86C358DF454704ADF6AF98A8B3135D',
  passport: '233092EB24424B98928696BE268640C5',
  residence: '6553EE440E7542669B54B596B19B2D9F',
} as const

export const ADULT_AGE = 18
export const DOCUMENT_REQUIRED_AGE = 14

export const nifRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i
export const nieRegex = /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i
export const passportRegex = /^(?!^0+$)[a-zA-Z0-9]{3,20}$/
// eslint-disable-next-line no-control-regex
export const emailRegex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i
/** La app actual pide segundo apellido en España salvo que el número tenga forma de NIE. */
const NIE_SHAPE = /^[A-Z][0-9]{7}[A-Z]$/

export const EMPTY_GUEST: GuestData = {
  name: '',
  surname1: '',
  surname2: '',
  identifierTypeId: '',
  identifier: '',
  supportNumber: '',
  bornDate: '',
  sexId: '',
  email: '',
  countryId: '',
  provinceId: '',
  municipalityId: '',
  city: '',
  address: '',
  postalCode: '',
  relationship: null,
}

/** Edad cumplida a `now` a partir de `yyyy-MM-dd`; undefined si no hay fecha válida. */
export function ageOn(bornDate: string, now: Date = new Date()): number | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bornDate)
  if (!m) return undefined
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  let age = now.getFullYear() - y
  if (now.getMonth() + 1 < mo || (now.getMonth() + 1 === mo && now.getDate() < d)) age -= 1
  return age
}

/** ManualGuestDataForm.utils.getGuestAgeRequirements */
export function ageRequirements(guestAge: number | undefined, isSpain: boolean) {
  const isAdult = guestAge !== undefined ? guestAge >= ADULT_AGE : true
  const isChild = guestAge !== undefined && !isAdult
  const isDocumentRequiredAge = guestAge !== undefined ? guestAge >= DOCUMENT_REQUIRED_AGE : true
  return { isAdult, isChild, isDocumentRequiredAge, isDocumentRequired: isSpain ? isDocumentRequiredAge : isAdult }
}

export function spainId(stay: Pick<Stay, 'registration'>): string | null {
  return stay.registration.catalogs.documentCountries.find((c) => c.isoCountry === 'ES')?.id ?? null
}

type RulesStay = Pick<Stay, 'registration'>

/** Qué campos se ven y cuáles son obligatorios para estos datos (ManualGuestDataForm + CountryFormInputs). */
export function fieldRules(stay: RulesStay, guest: GuestData, now: Date = new Date()): FieldRules {
  const rules: RegistrationRules = stay.registration.rules
  const isSpain = guest.countryId !== '' && guest.countryId === spainId(stay)
  const isForeign = guest.countryId !== '' && !isSpain
  const age = ageRequirements(ageOn(guest.bornDate, now), isSpain)
  const isDni = guest.identifierTypeId === IDENTIFIER_TYPE.dni
  const isNie = guest.identifierTypeId === IDENTIFIER_TYPE.residence
  const askSupport = isSpain && (isDni || isNie) && rules.ministerioInterior
  const mi = rules.ministerioInterior
  const on = (required: boolean) => ({ visible: true, required })
  const off = { visible: false, required: false }
  return {
    name: on(true),
    surname1: on(true),
    surname2: on(isSpain && !NIE_SHAPE.test(guest.identifier)),
    identifierTypeId: on(age.isDocumentRequired),
    identifier: on(age.isDocumentRequired),
    supportNumber: askSupport ? on(age.isDocumentRequiredAge) : off,
    bornDate: on(true),
    sexId: on(true),
    email: on(true),
    countryId: on(true),
    provinceId: isSpain ? on(true) : off,
    municipalityId: isSpain && mi ? on(true) : off,
    city: isForeign && mi ? on(true) : off,
    address: mi ? on(true) : off,
    postalCode: mi ? on(true) : off,
    relationship: age.isChild ? on(false) : off,
  } satisfies Record<GuestField, { visible: boolean; required: boolean }>
}

export type FieldError = 'required' | 'invalid'

/** validators.validateIdentifier: solo valida la forma en España, como hoy. */
export function isValidIdentifier(identifierTypeId: string, identifier: string): boolean {
  if (identifierTypeId === IDENTIFIER_TYPE.dni) return nifRegex.test(identifier) || nieRegex.test(identifier)
  if (identifierTypeId === IDENTIFIER_TYPE.residence) return nieRegex.test(identifier)
  if (identifierTypeId === IDENTIFIER_TYPE.passport) return passportRegex.test(identifier)
  return true
}

export const isValidSupportNumber = (value: string) => /^\w\d{8}$/.test(value) || /^\w{3}\d{6}$/.test(value)
export const isValidEmail = (value: string) => value.length === 0 || emailRegex.test(value)

/** Error de un campo, o null. Solo se validan los campos visibles. */
export function fieldError(field: GuestField, stay: RulesStay, guest: GuestData, now: Date = new Date()): FieldError | null {
  const rule = fieldRules(stay, guest, now)[field]
  if (!rule.visible) return null
  if (field === 'relationship') return null
  const value = guest[field].trim()
  if (value === '') return rule.required ? 'required' : null
  if ((field === 'name' || field === 'surname1') && value.length < 3) return 'invalid'
  const isSpain = guest.countryId === spainId(stay)
  if (field === 'identifier' && isSpain && !isValidIdentifier(guest.identifierTypeId, value)) return 'invalid'
  if (field === 'supportNumber' && !isValidSupportNumber(value.toUpperCase())) return 'invalid'
  if (field === 'email' && !isValidEmail(value)) return 'invalid'
  if (field === 'bornDate') {
    const age = ageOn(value, now)
    if (age === undefined || age < 0) return 'invalid'
  }
  return null
}

/** Errores de un grupo de campos (un paso del flujo). */
export function stepErrors(fields: GuestField[], stay: RulesStay, guest: GuestData, now: Date = new Date()): Partial<Record<GuestField, FieldError>> {
  const out: Partial<Record<GuestField, FieldError>> = {}
  for (const f of fields) {
    const e = fieldError(f, stay, guest, now)
    if (e) out[f] = e
  }
  return out
}
